import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import Marquee from "@/components/Marquee";
import Magnetic from "@/components/Magnetic";

export default function Hero() {
  return (
    <section
      className="relative pt-32 md:pt-40 pb-12 md:pb-20 overflow-hidden ink-grain"
      data-testid="hero-section"
    >
      {/* Glow blob */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 h-[600px] w-[900px] rounded-full opacity-50 blur-3xl"
        style={{
          background:
            "radial-gradient(closest-side, rgba(255,69,0,0.35), rgba(255,69,0,0.05) 60%, transparent 80%)",
        }}
      />
      {/* Grid lines */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage:
            "radial-gradient(ellipse at 50% 30%, black 30%, transparent 75%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-2 mb-6"
        >
          <span className="float-tag" data-testid="hero-badge">
            <span className="h-1.5 w-1.5 rounded-full bg-[#CCFF00] animate-pulse" />
            Now booking Q{Math.floor(new Date().getMonth() / 3) + 1} · {new Date().getFullYear()}
          </span>
          <span className="float-tag hidden sm:inline-flex">
            <Sparkles className="h-3 w-3 text-[#FF4500]" />
            AEO + GEO ready
          </span>
        </motion.div>

        <h1
          className="font-display font-black uppercase leading-[0.85] tracking-[-0.04em] text-[14vw] sm:text-[12vw] md:text-[9.5vw] lg:text-[8.5rem] xl:text-[10rem]"
          data-testid="hero-heading"
        >
          {["NEXT", "LEVEL", "DIGITAL"].map((word, i) => (
            <span
              key={word}
              className={`block hero-reveal-line hero-reveal-line-${i + 1}`}
            >
              {word === "DIGITAL" ? (
                <>
                  <span>{word}</span>
                  <span className="text-[#FF4500] glow-orange">.</span>
                </>
              ) : i === 1 ? (
                <span className="text-stroke">{word}</span>
              ) : (
                <span>{word}</span>
              )}
            </span>
          ))}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
          <div
            className="lg:col-span-6 lg:col-start-7 hero-reveal-subtext"
          >
            <p className="text-lg md:text-xl text-white/75 leading-relaxed max-w-xl">
              Inkaa is a small, savage studio. We build websites that load before
              your coffee does, marketing engines that compound, and SEO+AEO+GEO
              systems that make your brand the answer — across Google, ChatGPT,
              Perplexity & Gemini.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Magnetic strength={0.2}>
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
                  Start a project <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Magnetic>
              <Magnetic strength={0.2}>
                <Link to="/work" className="btn-ghost" data-testid="hero-cta-secondary">
                  See the work
                </Link>
              </Magnetic>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="relative mt-16 md:mt-24 border-y border-white/10 py-5 bg-black/40">
        <Marquee
          items={[
            "WEB DEVELOPMENT",
            "MARKETING",
            "SEO",
            "AEO",
            "GEO",
            "BRAND SYSTEMS",
          ]}
          variant="stroke"
        />
      </div>
    </section>
  );
}
